/**
 * Configuración del Sistema de Capas de Exocortex
 *
 * Las capas representan una jerarquía de necesidades inspirada en Maslow.
 * Cada capa superior requiere que la anterior esté completa para "desbloquearse",
 * reforzando la importancia de atender primero lo básico.
 */

import type { Layer, LayerConfig } from '@/core/types';

/**
 * Configuración completa de cada capa
 */
export const LAYERS: Record<Layer, LayerConfig> = {
  survival: {
    id: 'survival',
    name: 'Supervivencia',
    description: 'Funciones vitales y cuidados críticos que no pueden posponerse',
    color: {
      bg: 'bg-rose-500',
      text: 'text-rose-400',
      border: 'border-rose-700',
      accent: '#f43f5e',
      bgTransparent: 'bg-rose-900/30',
      hover: 'hover:bg-rose-900/50',
      ring: 'ring-rose-500',
    },
    icon: 'ShieldAlert',
    priority: 1,
    rationale:
      'Basado en la Pirámide de Maslow: las necesidades fisiológicas y de seguridad deben satisfacerse antes de poder funcionar en niveles superiores. Sin medicación, hidratación o cuidado de dependientes, el sistema nervioso entra en modo de supervivencia y reduce la capacidad cognitiva.',
    unlockCondition: null, // Siempre disponible
  },

  maintenance: {
    id: 'maintenance',
    name: 'Mantenimiento',
    description: 'Estabilidad del entorno y producción sostenible',
    color: {
      bg: 'bg-amber-500',
      text: 'text-amber-400',
      border: 'border-amber-700',
      accent: '#f59e0b',
      bgTransparent: 'bg-amber-900/30',
      hover: 'hover:bg-amber-900/50',
      ring: 'ring-amber-500',
    },
    icon: 'Activity',
    priority: 2,
    rationale:
      'El estado de flujo y la productividad sostenida requieren un entorno estable. La investigación sobre función ejecutiva demuestra que el desorden ambiental consume recursos cognitivos limitados. El deep work requiere un contexto predecible.',
    unlockCondition: 'survival',
  },

  growth: {
    id: 'growth',
    name: 'Crecimiento',
    description: 'Desarrollo personal, aprendizaje y proyectos creativos',
    color: {
      bg: 'bg-emerald-500',
      text: 'text-emerald-400',
      border: 'border-emerald-700',
      accent: '#10b981',
      bgTransparent: 'bg-emerald-900/30',
      hover: 'hover:bg-emerald-900/50',
      ring: 'ring-emerald-500',
    },
    icon: 'Sprout',
    priority: 3,
    rationale:
      'El aprendizaje y la creatividad florecen cuando el cerebro no está en modo de supervivencia ni preocupado por el caos ambiental. La neuroplasticidad se optimiza con seguridad fisiológica establecida y entorno controlado.',
    unlockCondition: 'maintenance',
  },
} as const;

/**
 * Orden de las capas (de menor a mayor prioridad)
 */
export const LAYER_ORDER: Layer[] = ['survival', 'maintenance', 'growth'];

/**
 * Obtener configuración de una capa
 */
export function getLayerConfig(layer: Layer): LayerConfig {
  return LAYERS[layer];
}

/**
 * Verificar si una capa está desbloqueada
 */
export function isLayerUnlocked(
  layer: Layer,
  completedLayers: Set<Layer>
): boolean {
  const config = LAYERS[layer];

  // Survival siempre está desbloqueada
  if (config.unlockCondition === null) {
    return true;
  }

  // Verificar si la capa requerida está completa
  return completedLayers.has(config.unlockCondition);
}

/**
 * Obtener el nombre localizado de una capa
 */
export function getLayerName(layer: Layer): string {
  return LAYERS[layer].name;
}

/**
 * Obtener las clases de color para una capa
 */
export function getLayerColors(layer: Layer) {
  return LAYERS[layer].color;
}
